import { z } from 'zod/v4'
import { ANCHOS, CLAVE, COLECCIONES_DE_RELACION, FAMILIAS_DE_ARCHIVO, NIVELES_DE_LUGAR, TIPOS_DE_DATO, VALOR_DE_OPCION, VISIBILIDADES } from './catalogo'

/**
 * El esquema con el que se valida lo que **propone la IA** en un plan (contrato
 * `planificar`). Es la forma, no el sentido: después pasa por `revisarDefinicion`, que mira
 * claves repetidas, destinos coherentes y mínimos contra máximos.
 *
 * Dos decisiones que vienen del §28:
 * - **Sin `z.record`**: el SDK lo traduce a un objeto donde no se puede escribir nada y el
 *   modelo devuelve `{}`. Las opciones son una lista de objetos.
 * - **Sin recursión**: el anidamiento se escribe en tres niveles explícitos (campo suelto,
 *   grupo, repetidor), que es además la regla del catálogo. Una gramática con `z.lazy` no la
 *   acepta la salida estricta de todos los proveedores.
 */
const TIPOS_SUELTOS = TIPOS_DE_DATO.filter((t) => t !== 'grupo' && t !== 'repetidor') as [Exclude<(typeof TIPOS_DE_DATO)[number], 'grupo' | 'repetidor'>, ...Exclude<(typeof TIPOS_DE_DATO)[number], 'grupo' | 'repetidor'>[]]

const comun = {
  clave: z.string().regex(CLAVE),
  etiqueta: z.string().min(1).max(120),
  ayuda: z.string().max(300).nullable().optional(),
  obligatorio: z.boolean().nullable().optional(),
  ancho: z.enum(ANCHOS).nullable().optional(),
  filtrable: z.boolean().nullable().optional(),
}

const configuracion = {
  minimo: z.number().nullable().optional(),
  maximo: z.number().nullable().optional(),
  decimales: z.number().int().min(0).max(4).nullable().optional(),
  unidad: z.string().max(20).nullable().optional(),
  esMoneda: z.boolean().nullable().optional(),
  opciones: z.array(z.object({ valor: z.string().regex(VALOR_DE_OPCION), etiqueta: z.string().min(1).max(120) })).max(100).nullable().optional(),
  tipos: z.array(z.enum(FAMILIAS_DE_ARCHIVO)).nullable().optional(),
  visibilidad: z.enum(VISIBILIDADES).nullable().optional(),
  destino: z.object({ coleccion: z.enum(COLECCIONES_DE_RELACION), tipo: z.string().max(60).nullable().optional(), taxonomia: z.string().max(60).nullable().optional() }).nullable().optional(),
  multiple: z.boolean().nullable().optional(),
  paises: z.array(z.string().regex(/^[A-Z]{2}$/)).nullable().optional(),
  nivel: z.enum(NIVELES_DE_LUGAR).nullable().optional(),
}

export const esquemaZodDeCampoSuelto = z.object({ ...comun, tipo: z.enum(TIPOS_SUELTOS), ...configuracion })
export const esquemaZodDeGrupo = z.object({ ...comun, tipo: z.literal('grupo'), campos: z.array(esquemaZodDeCampoSuelto).min(1).max(40) })
export const esquemaZodDeRepetidor = z.object({
  ...comun,
  tipo: z.literal('repetidor'),
  campos: z.array(z.union([esquemaZodDeCampoSuelto, esquemaZodDeGrupo])).min(1).max(40),
  minimo: z.number().int().min(0).nullable().optional(),
  maximo: z.number().int().min(1).nullable().optional(),
})

export const esquemaZodDeCampo = z.union([esquemaZodDeCampoSuelto, esquemaZodDeGrupo, esquemaZodDeRepetidor])
export const esquemaZodDeDefinicion = z.array(esquemaZodDeCampo).max(40)
